import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ExpandableSection from "@components/animations/ExpandableSection";
import CircleButton from "@components/buttons/CircleButton";
import BasicCard from "@components/cards/BasicCard";
import MedicationForm from "@components/forms/MedicationForm";
import MedicationTracker from "@components/MedicationTracker";
import Modal, { ModalElement } from "@components/Modal";
import Icon from "@components/text/Icon";
import Tracker from "@components/trackers/Tracker";
import styles from "@components/trackers/Tracker.module.css";
import TrackerPopover from "@components/trackers/TrackerPopover";
import BodyCopy from "@components/typography/BodyCopy";
import useDestroyMedication from "@hooks/mutations/useDestroyMedication";
import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import useOpener from "@hooks/useOpener";
import {
  GetJourneyWithCareProfileQuery,
  GetJourneyWithDocumentsQuery,
  GetPdfDownloadDocument,
  GetPdfDownloadQuery,
  GetPdfDownloadQueryVariables,
  MedicationFormatEnum,
  MedicationFragment,
  MedicationKindEnum,
  PdfTypeEnum,
  TrackerStatusEnum
} from "@typing/Generated";
import { downloadFile } from "@utils/downloadUtils";
import { medicationsBatched, sortByTimestamp } from "@utils/modelUtils";

type Props = {
  journey: GetJourneyWithCareProfileQuery["journey"] | GetJourneyWithDocumentsQuery["journey"];
};

type MedicationProps = {
  handleDestroy: (medication: MedicationFragment) => void;
  handleEdit: (medication: MedicationFragment) => void;
  medication: MedicationFragment;
};

type MedicationDescriptionProps = {
  description: string | null;
  labelKey: string;
  visible: boolean;
};

const MedicationDescription = ({ description, labelKey, visible }: MedicationDescriptionProps) => {
  if (!visible) return null;

  return (
    <div className={styles.description}>
      <div className="no-wrap">
        <BodyCopy bold color="primary">
          <FormattedMessage id={labelKey} />
        </BodyCopy>
      </div>

      <BodyCopy bold color="gray-500">
        {description}
      </BodyCopy>
    </div>
  );
};

const Medication = ({ handleDestroy, handleEdit, medication }: MedicationProps) => {
  const opener = useOpener();
  const intl = useIntl();

  const displayDetailsToggle = useMemo(() => {
    if (
      medication.reasonForTaking ||
      medication.dosage ||
      medication.instructions ||
      medication.format.formatOption ||
      medication.prescriber ||
      medication.refillFrequency ||
      medication.pharmacy ||
      medication.prescriptionNumber ||
      medication.paymentType ||
      medication.notes
    ) {
      return true;
    }
    return false;
  }, [medication]);

  return (
    <BasicCard boxShadow hover="none" padding="none">
      <div data-test="medication">
        <div className={styles.item}>
          <div className={styles.titleAndIcon}>
            {medication.kind === MedicationKindEnum.PRESCRIPTION ? (
              <Icon color="primary" icon="prescription" />
            ) : (
              <BodyCopy bold color="primary">
                {medication.kind === MedicationKindEnum.OVER_THE_COUNTER ? (
                  <FormattedMessage id="pages.client.medications.list.oTC" />
                ) : (
                  <FormattedMessage id="pages.client.medications.list.vAndS" />
                )}
              </BodyCopy>
            )}
            <BodyCopy bold color="primary">
              {medication.name}
            </BodyCopy>
          </div>
          <div className={styles.actions}>
            {displayDetailsToggle && (
              <CircleButton
                ariaLabelKey="accessibility.ariaLabels.toggle"
                data-test="details"
                fill="clear"
                icon={opener.isOpen ? "chevronUp" : "chevronDown"}
                onClick={opener.toggle}
                size="medium"
              />
            )}

            <TrackerPopover handleDestroy={() => handleDestroy(medication)} handleEdit={() => handleEdit(medication)} />
          </div>
        </div>
        <ExpandableSection isOpen={opener.isOpen} key={`medicationDescriptions${medication.id}`}>
          <div className={styles.descriptions}>
            <MedicationDescription
              description={medication.reasonForTaking}
              labelKey="pages.client.medications.list.indication"
              visible={!!medication.reasonForTaking}
            />
            <MedicationDescription
              description={medication.dosage}
              labelKey="pages.client.medications.list.dosageAndFrequency"
              visible={!!medication.dosage}
            />
            <MedicationDescription
              description={medication.instructions}
              labelKey="pages.client.medications.list.instructions"
              visible={!!medication.instructions}
            />
            <MedicationDescription
              description={
                medication.format.formatOption === MedicationFormatEnum.OTHER && medication.format.other
                  ? medication.format.other
                  : medication.format.formatOption
              }
              labelKey="pages.client.medications.list.format"
              visible={!!(medication.format.other || medication.format.formatOption)}
            />
            <MedicationDescription
              description={medication.prescriber}
              labelKey="pages.client.medications.list.prescriber"
              visible={!!medication.prescriber}
            />
            <MedicationDescription
              description={medication.refillFrequency}
              labelKey="pages.client.medications.list.refillFrequency"
              visible={!!medication.refillFrequency}
            />
            <MedicationDescription
              description={medication.pharmacy}
              labelKey="pages.client.medications.list.pharmacy"
              visible={!!medication.pharmacy}
            />
            <MedicationDescription
              description={medication.prescriptionNumber}
              labelKey="pages.client.medications.list.prescriptionNumber"
              visible={!!medication.prescriptionNumber}
            />
            <MedicationDescription
              description={
                medication.paymentType
                  ? intl.formatMessage({
                      id: `pages.client.medications.list.paymentTypeOptions.${medication.paymentType}`
                    })
                  : ""
              }
              labelKey="pages.client.medications.list.paymentType"
              visible={!!medication.paymentType}
            />
            <MedicationDescription
              description={medication.notes}
              labelKey="pages.client.medications.list.notes"
              visible={!!medication.notes}
            />
          </div>
        </ExpandableSection>
      </div>
    </BasicCard>
  );
};

const Medications = ({ journey }: Props) => {
  const intl = useIntl();
  const trackerOpener = useOpener();
  const addOpener = useOpener();
  const medicationModalRef = useRef<ModalElement>(null);
  const medicalRef = useRef<HTMLDivElement>(null);
  const [editingMedication, setEditingMedication] = useState<MedicationFragment | undefined>(undefined);
  const batchedMedications = useMemo(() => medicationsBatched(journey.medications), [journey.medications]);

  useEffect(() => {
    if (location.hash.includes("medication") && medicalRef.current) {
      medicalRef.current.scrollIntoView({ behavior: "smooth" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const getPdfDownload = useLazyQueryWithPromise<GetPdfDownloadQuery, GetPdfDownloadQueryVariables>(
    GetPdfDownloadDocument
  );

  const handleDownload = useCallback(
    (event: MouseEvent<HTMLIonButtonElement>) => {
      event.preventDefault();
      getPdfDownload({
        fetchPolicy: "no-cache",
        variables: { journeyId: journey.id, type: PdfTypeEnum.MEDICATION_TRACKER }
      }).then(async response => {
        downloadFile(response.data.pdfDownload.url, "grayce-medications.pdf");
      });
    },
    [getPdfDownload, journey.id]
  );

  const handleMedicationError = () => {
    if (medicationModalRef.current) {
      medicationModalRef.current.scrollToTop();
    }
  };

  const destroyMedication = useDestroyMedication(journey.id);

  return (
    <div className={styles.trackerContainer} ref={medicalRef}>
      <Tracker
        canDownload={journey.medications.length > 0}
        handleDownload={handleDownload}
        kind="medical"
        opener={journey.medicationTrackerStatus === TrackerStatusEnum.COMPLETED ? addOpener : trackerOpener}
        trackerStatus={journey.medicationTrackerStatus}
      >
        <div className={styles.items}>
          {sortByTimestamp(journey.medications, medication => medication.createdAt).map(medication => (
            <Medication
              handleDestroy={m => {
                destroyMedication({
                  id: m.id
                });
              }}
              handleEdit={m => {
                setEditingMedication(m);
                addOpener.open();
              }}
              key={medication.id}
              medication={medication}
            />
          ))}
        </div>
      </Tracker>
      <MedicationTracker
        batchedMedications={batchedMedications}
        journey={journey}
        medicationTrackerOpener={trackerOpener}
        restartWizard
      />
      <Modal
        opener={addOpener}
        ref={medicationModalRef}
        title={
          editingMedication
            ? intl.formatMessage({ id: "models.client.medication.updateMedication" })
            : intl.formatMessage({ id: "models.client.medication.addNewMedication" })
        }
      >
        {({ modalDisplayed }) => (
          <MedicationForm
            autofocus={modalDisplayed}
            journeyId={journey.id}
            medication={editingMedication}
            onCancel={addOpener.close}
            onError={handleMedicationError}
            onSuccess={addOpener.close}
          />
        )}
      </Modal>
    </div>
  );
};

export default Medications;
